import { useEffect, useState } from "react";
import { doc, getDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { db, signInAnonymouslyForField } from "../firebase";
import { S, C } from "../ui";
import lockupLight from "../assets/lockup-light.svg";

const FACILITY_KEY = "edifice_staff_facility";
const SESSION_KEY = "edifice_staff_session";

// Phase 11: staff reached via the home screen (not a QR scan) have no
// assetId/facilityId in the URL, so they identify their building with a
// facility access code instead — a persistent, shareable code a Facility
// Admin generates once (see FacilityAdminDashboard.jsx), stored in
// facilityAccessCodes/{code} (mirrors inviteCodes/{code}'s shape exactly,
// see firestore.rules). Name + PIN after that is identical to the
// existing QR flow (see FieldFlow.jsx) — this is purely a different way
// to arrive at "I know my facilityId," not a different security model.
export default function StaffAuthScreen({ onLogin }) {
  const [ready, setReady] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(!localStorage.getItem(FACILITY_KEY));
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const [checkingCode, setCheckingCode] = useState(false);
  const [facilityId, setFacilityId] = useState(null);
  const [facilityName, setFacilityName] = useState("");
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");

  useEffect(() => { signInAnonymouslyForField().then(() => setReady(true)); }, []);

  // Skip straight past the code step if this device already resolved one —
  // but re-check the code is still live first. Without this, a revoked code
  // (see FacilityAdminDashboard.jsx's "Revoke" button) would only ever block
  // brand-new devices: this cache used to just remember the facilityId
  // forever with no way to un-remember it, so a device that resolved the
  // code once stayed past this screen even after the code was deleted.
  useEffect(() => {
    if (!ready || cacheChecked) return;
    const cached = localStorage.getItem(FACILITY_KEY);
    if (!cached) { setCacheChecked(true); return; }
    try {
      const { facilityId: fid, facilityName: fname, code: cachedCode } = JSON.parse(cached);
      if (!cachedCode) { localStorage.removeItem(FACILITY_KEY); setCacheChecked(true); return; } // pre-revocation cache shape, no code to re-check
      getDoc(doc(db, "facilityAccessCodes", cachedCode)).then(snap => {
        if (snap.exists()) { setFacilityId(fid); setFacilityName(fname); }
        else localStorage.removeItem(FACILITY_KEY); // revoked since — fall back to the code screen
        setCacheChecked(true);
      });
    } catch { localStorage.removeItem(FACILITY_KEY); setCacheChecked(true); }
  }, [ready, cacheChecked]);

  useEffect(() => {
    if (!ready || !facilityId) return;
    return onSnapshot(query(collection(db, "workers"), where("facilityId", "==", facilityId)), s =>
      setWorkers(s.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [ready, facilityId]);

  const submitCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { setCodeErr("Enter your facility's access code."); return; }
    setCheckingCode(true); setCodeErr("");
    try {
      const snap = await getDoc(doc(db, "facilityAccessCodes", c));
      if (!snap.exists()) { setCodeErr("That code isn't valid — check with your facility admin."); setCheckingCode(false); return; }
      // facilityName is denormalized onto this doc (see
      // FacilityAdminDashboard.jsx's generateStaffAccessCode) precisely so
      // this anonymous session never needs to read facilities/{id}
      // directly, which it isn't authorized to do.
      const { facilityId: fid, facilityName: fname } = snap.data();
      localStorage.setItem(FACILITY_KEY, JSON.stringify({ facilityId: fid, facilityName: fname, code: c }));
      setFacilityId(fid); setFacilityName(fname);
    } catch (e) {
      console.error("[StaffAuthScreen] submitCode failed:", e);
      setCodeErr("Something went wrong — try again.");
    }
    setCheckingCode(false);
  };

  const verifyPin = () => {
    if (pin !== selectedWorker.pin) { setPinErr("Wrong PIN"); setPin(""); return; }
    const session = { facilityId, workerId: selectedWorker.id };
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    onLogin(session);
  };

  const forgetFacility = () => {
    localStorage.removeItem(FACILITY_KEY);
    setFacilityId(null); setFacilityName(""); setCode("");
  };

  if (!ready || !cacheChecked) return <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Loading…</div>;

  if (!facilityId) return (
    <div style={S.center}><div style={S.card}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
        <img src={lockupLight} alt="Edifice View" style={{ height: 36 }} />
      </div>
      <div style={S.title}>Staff Sign In</div>
      <div style={S.sub}>Enter the access code your facility admin gave you.</div>
      {codeErr && <div style={S.err}>{codeErr}</div>}
      <input style={{ ...S.input, textTransform: "uppercase" }} placeholder="Facility access code" value={code}
        onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === "Enter" && submitCode()} autoFocus />
      <button style={S.btn()} disabled={checkingCode} onClick={submitCode}>{checkingCode ? "Checking…" : "Continue →"}</button>
    </div></div>
  );

  if (!selectedWorker) return (
    <div style={S.center}><div style={S.card}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>{facilityName}</div>
      <div style={S.sub}>Who's this?</div>
      {workers.length === 0 && <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>No staff set up for this facility yet — ask your admin.</div>}
      {workers.map(w => (
        <div key={w.id} onClick={() => setSelectedWorker(w)}
          style={{ padding: "12px 14px", borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.bg, marginBottom: 8, cursor: "pointer" }}>
          <div style={{ fontWeight: 500 }}>{w.name}</div>
          <div style={{ fontSize: 12, color: C.muted }}>{w.trade}</div>
        </div>
      ))}
      <button style={S.ghostBtn} onClick={forgetFacility}>← Wrong facility?</button>
    </div></div>
  );

  return (
    <div style={S.center}><div style={S.card}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Hi, {selectedWorker.name.split(" ")[0]}</div>
      {pinErr && <div style={S.err}>{pinErr}</div>}
      <input style={S.input} type="password" placeholder="Your PIN" value={pin} onChange={e => setPin(e.target.value)}
        onKeyDown={e => e.key === "Enter" && verifyPin()} autoFocus maxLength={8} />
      <button style={S.btn()} onClick={verifyPin}>Continue →</button>
      <button style={S.ghostBtn} onClick={() => { setSelectedWorker(null); setPin(""); setPinErr(""); }}>← Back</button>
    </div></div>
  );
}

export function getStaffSession() {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function clearStaffSession() {
  localStorage.removeItem(SESSION_KEY);
}
